// The active order queue (2026-10-22): today's unprepared sales plus kitchen
// tickets sent from open table tabs. Shared by the main app (model.js) and the
// Queue Screen popup (kds-display.js), so both load and update it the same way.
// Ticket queue ids are "kt-<uuid>" so any window can tell a ticket from a sale
// by its id alone (broadcasts carry only ids).

import { todayIn, zonedToDate, addDays } from './scheduleTime.js';

export const TICKET_PREFIX = 'kt-';
export const isTicketId = (id) => typeof id === 'string' && id.startsWith(TICKET_PREFIX);

// "Today" on the BUSINESS's calendar. The main app passes its timezone and
// caches it for the Queue Screen popup, which reads it back from localStorage.
const TZ_KEY = 'pointbunny_business_tz';
const todayBounds = (tz) => {
  if (tz) { try { localStorage.setItem(TZ_KEY, tz); } catch { /* private mode */ } }
  let zone = tz;
  if (!zone) { try { zone = localStorage.getItem(TZ_KEY); } catch { zone = null; } }
  zone = zone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const day = todayIn(zone);
  return {
    start: zonedToDate(day, '00:00', zone).toISOString(),
    end: new Date(zonedToDate(addDays(day, 1), '00:00', zone).getTime() - 1).toISOString(),
  };
};

export const loadKitchenQueue = async function (supabase, businessId, tz = null) {
  const { start, end } = todayBounds(tz);
  const [sales, tickets] = await Promise.all([
    supabase
      .from('sales')
      .select('id, sale_date, items, total_price, order_type, ticket_number, ready_at, table_name')
      .eq('user_id', businessId)
      .gte('sale_date', start)
      .lte('sale_date', end)
      .is('prepared_at', null)
      .is('voided_at', null)
      .order('sale_date', { ascending: true }),
    // Tolerates the tables migration not being run yet (no table = no tickets).
    supabase
      .from('kitchen_tickets')
      .select('id, tab_id, sent_at, items, table_name, ready_at')
      .eq('business_id', businessId)
      .gte('sent_at', start)
      .is('prepared_at', null)
      .order('sent_at', { ascending: true }),
  ]);
  if (sales.error) throw sales.error;
  const fromSales = sales.data.map((row) => ({
    id: row.id,
    saleDate: row.sale_date,
    items: row.items,
    startedAt: new Date(row.sale_date).getTime(),
    totalPrice: Number(row.total_price),
    orderType: row.order_type ?? 'dine-in',
    ticketNumber: row.ticket_number ?? null,
    tableName: row.table_name ?? null,
    readyAt: row.ready_at ? new Date(row.ready_at).getTime() : null,
  }));
  const fromTickets = (tickets.error ? [] : tickets.data).map(ticketToQueueItem);
  return [...fromSales, ...fromTickets].sort((a, b) => a.startedAt - b.startedAt);
};

// A kitchen_tickets row (as returned by tab_send) as a queue item.
export const ticketToQueueItem = (row) => ({
  id: TICKET_PREFIX + row.id,
  saleDate: row.sent_at,
  items: row.items ?? [],
  startedAt: new Date(row.sent_at).getTime(),
  totalPrice: 0,
  orderType: 'dine-in',
  ticketNumber: null,
  tableName: row.table_name ?? null,
  fromTab: true,
  tabId: row.tab_id ?? null,
  readyAt: row.ready_at ? new Date(row.ready_at).getTime() : null,
});

// patch: { prepared_at, timed_out } (done) or { ready_at } (ready / not ready).
export const setQueueItemState = async function (supabase, businessId, id, patch) {
  const q = isTicketId(id)
    ? supabase.from('kitchen_tickets').update(patch).eq('id', id.slice(TICKET_PREFIX.length)).eq('business_id', businessId)
    : supabase.from('sales').update(patch).eq('id', id).eq('user_id', businessId);
  const { error } = await q;
  if (error) throw error;
};
